/**
 * Design System Tokens
 *
 * This file contains all design tokens for the application including colors,
 * typography, spacing, borders, shadows, and transitions.
 *
 * Brand palette (2026 rebrand):
 *   - Primary / Accent: violeta (#7c3aed) — sidebar activo, links, indicadores
 *   - Brand (CTA principal): verde lima (#16a34a) — botones de acción positiva
 *   - Navy: azul vibrante (#0b2f74 → #1c4ed8) — sidebar, fondos profundos
 *   - Neutral: slate (mantiene escalas existentes para textos/bordes)
 *
 * Contraste objetivo: WCAG 2.1 AA en todas las combinaciones documentadas.
 */

/**
 * Light mode design tokens (default theme)
 */
export const tokens = {
  colors: {
    // Acento principal (violeta) — variante "primary" de Button, items activos,
    // enlaces. Reemplaza al indigo previo. Contraste sobre blanco: 7.55:1 (600).
    primary: {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
    },
    /** Alias semántico del violeta. Mismo valor que `primary`. */
    accent: {
      50: '#f5f3ff',
      100: '#ede9fe',
      200: '#ddd6fe',
      400: '#a78bfa',
      500: '#8b5cf6',
      600: '#7c3aed',
      700: '#6d28d9',
    },
    /**
     * Brand verde lima — usado como CTA principal (botones de "Conectar",
     * "Crear", "Guardar"). Contraste blanco/600: 4.65:1.
     */
    brand: {
      50: '#f0fdf4',
      100: '#dcfce7',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
    },
    /**
     * Navy — azul oscuro vibrante para el shell (sidebar, gradients de marca).
     * El gradient sugerido va de navy.900 → navy.700.
     */
    navy: {
      50: '#eff6ff',
      100: '#dbeafe',
      500: '#3b82f6',
      600: '#1c4ed8',
      700: '#0f3a96',
      800: '#0b2f74',
      900: '#0a1e4f',
      950: '#06112e',
    },
    // Neutral colors - used for text, backgrounds, and borders
    neutral: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    // Semantic colors - used for status and feedback
    success: { light: '#dcfce7', main: '#22c55e', dark: '#15803d' },
    warning: { light: '#fef3c7', main: '#f59e0b', dark: '#92400e' },
    error: { light: '#fee2e2', main: '#ef4444', dark: '#b91c1c' },
    info: { light: '#dbeafe', main: '#3b82f6', dark: '#1e40af' },
  },

  /**
   * Tokens semánticos derivados de la paleta.
   *
   * Preferí estos sobre tocar `colors.*` directamente cuando el significado
   * sea claro (ej. fondo de tarjeta, borde sutil, CTA principal). Así los
   * cambios futuros de marca solo tocan este bloque.
   */
  semantic: {
    // Superficies
    surface: '#ffffff',
    surfaceMuted: '#f8fafc',
    surfaceSubtle: '#f1f5f9',
    surfaceDeep: '#ffffff', // sidebar base (white in new design)
    surfaceDeeper: '#ffffff', // sidebar (white)
    // Texto
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    textMuted: '#64748b',
    textSubtle: '#94a3b8',
    textOnDark: '#e2e8f0',
    textOnDarkMuted: '#94a3b8',
    textOnAccent: '#ffffff',
    // Bordes
    border: '#e2e8f0',
    borderStrong: '#cbd5e1',
    borderOnDark: 'rgba(148, 163, 184, 0.18)',
    // CTAs
    ctaPrimary: '#16a34a',
    ctaPrimaryHover: '#15803d',
    ctaPrimarySoft: '#dcfce7',
    // Accent (violeta)
    accent: '#7c3aed',
    accentHover: '#6d28d9',
    accentSoft: '#f5f3ff',
    accentRing: 'rgba(124, 58, 237, 0.35)',
    // Estados (compatibles con tablas/gráficos)
    chartA: '#7c3aed', // violeta
    chartB: '#16a34a', // verde
    chartC: '#3b82f6', // azul info
    chartD: '#f59e0b', // ámbar
    chartE: '#ef4444', // rojo
  },

  typography: {
    fontFamily: {
      sans: "'DM Sans', system-ui, -apple-system, sans-serif",
      mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    fontSize: {
      xs: '0.75rem', // 12px
      sm: '0.875rem', // 14px
      base: '1rem', // 16px
      lg: '1.125rem', // 18px
      xl: '1.25rem', // 20px
      '2xl': '1.5rem', // 24px
      '3xl': '1.875rem', // 30px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  spacing: {
    0: '0',
    1: '0.25rem', // 4px
    2: '0.5rem', // 8px
    3: '0.75rem', // 12px
    4: '1rem', // 16px
    5: '1.25rem', // 20px
    6: '1.5rem', // 24px
    7: '1.75rem', // 28px
    8: '2rem', // 32px
    10: '2.5rem', // 40px
    12: '3rem', // 48px
    16: '4rem', // 64px
  },

  borderRadius: {
    none: '0',
    sm: '0.25rem', // 4px
    md: '0.5rem', // 8px
    lg: '0.75rem', // 12px
    xl: '1rem', // 16px
    '2xl': '1.25rem', // 20px
    full: '9999px',
  },

  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
    /** Sombra acentuada en violeta para hovers de tarjetas principales. */
    accent: '0 12px 28px -6px rgba(124, 58, 237, 0.28)',
  },

  transitions: {
    fast: '150ms ease',
    normal: '200ms ease',
    slow: '300ms ease',
  },
} as const;

/**
 * Dark mode token overrides
 *
 * These tokens override the light mode tokens when dark mode is active.
 * The neutral scale is inverted to provide proper contrast in dark mode.
 */
export const darkTokens = {
  colors: {
    neutral: {
      50: '#0f172a',
      100: '#1e293b',
      200: '#334155',
      300: '#475569',
      400: '#64748b',
      500: '#94a3b8',
      600: '#cbd5e1',
      700: '#e2e8f0',
      800: '#f1f5f9',
      900: '#f8fafc',
    },
  },
  semantic: {
    surface: '#0f172a',
    surfaceMuted: '#1e293b',
    surfaceSubtle: '#0a1e4f',
    surfaceDeep: '#06112e',
    surfaceDeeper: '#020617',
    textPrimary: '#f8fafc',
    textSecondary: '#e2e8f0',
    textMuted: '#94a3b8',
    textSubtle: '#64748b',
    textOnDark: '#e2e8f0',
    textOnDarkMuted: '#94a3b8',
    textOnAccent: '#ffffff',
    border: 'rgba(148, 163, 184, 0.18)',
    borderStrong: 'rgba(148, 163, 184, 0.28)',
    borderOnDark: 'rgba(148, 163, 184, 0.18)',
    ctaPrimary: '#22c55e',
    ctaPrimaryHover: '#16a34a',
    ctaPrimarySoft: 'rgba(34, 197, 94, 0.18)',
    accent: '#a78bfa',
    accentHover: '#8b5cf6',
    accentSoft: 'rgba(167, 139, 250, 0.18)',
    accentRing: 'rgba(167, 139, 250, 0.45)',
    chartA: '#a78bfa',
    chartB: '#34d399',
    chartC: '#60a5fa',
    chartD: '#fbbf24',
    chartE: '#f87171',
  },
} as const;

// Type exports for type-safe token usage
export type Tokens = typeof tokens;
export type DarkTokens = typeof darkTokens;
export type ColorTokens = Tokens['colors'];
export type SemanticTokens = Tokens['semantic'];
export type TypographyTokens = Tokens['typography'];
export type SpacingTokens = Tokens['spacing'];
export type BorderRadiusTokens = Tokens['borderRadius'];
export type ShadowTokens = Tokens['shadows'];
export type TransitionTokens = Tokens['transitions'];
